import React, { ButtonHTMLAttributes } from 'react';
import styled, { css } from 'styled-components';
import { theme } from '../../styles/theme';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'text';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const getVariantStyles = (variant: ButtonVariant) => {
  switch (variant) {
    case 'primary':
      return css`
        background-color: ${theme.colors.primary};
        color: ${theme.colors.white};
        &:hover:not(:disabled) {
          background-color: ${theme.colors.primaryDark};
        }
      `;
    case 'secondary':
      return css`
        background-color: ${theme.colors.secondary};
        color: ${theme.colors.text};
        &:hover:not(:disabled) {
          background-color: #E8E8D0;
        }
      `;
    case 'outline':
      return css`
        background-color: transparent;
        color: ${theme.colors.primary};
        border: 1px solid ${theme.colors.primary};
        &:hover:not(:disabled) {
          background-color: rgba(29, 157, 231, 0.1);
        }
      `;
    case 'text':
      return css`
        background-color: transparent;
        color: ${theme.colors.primary};
        &:hover:not(:disabled) {
          background-color: rgba(29, 157, 231, 0.1);
        }
      `;
    default:
      return '';
  }
};

const getSizeStyles = (size: ButtonSize) => {
  switch (size) {
    case 'sm':
      return css`
        padding: ${theme.space.xs} ${theme.space.sm};
        font-size: ${theme.fontSizes.sm};
      `;
    case 'md':
      return css`
        padding: ${theme.space.sm} ${theme.space.md};
        font-size: ${theme.fontSizes.md};
      `;
    case 'lg':
      return css`
        padding: ${theme.space.md} ${theme.space.lg};
        font-size: ${theme.fontSizes.lg};
      `;
    default:
      return '';
  }
};

const StyledButton = styled.button<{
  variant: ButtonVariant;
  size: ButtonSize;
  fullWidth: boolean;
  isLoading: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.radii.md};
  font-weight: ${theme.fontWeights.medium};
  transition: ${theme.transitions.default};
  cursor: pointer;
  border: none;
  outline: none;
  width: ${props => props.fullWidth ? '100%' : 'auto'};
  opacity: ${props => props.isLoading ? 0.7 : 1};
  
  ${props => getVariantStyles(props.variant)}
  ${props => getSizeStyles(props.size)}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  & > *:not(:last-child) {
    margin-right: ${theme.space.xs};
  }
`;

const LoadingSpinner = styled.div`
  width: 1em;
  height: 1em;
  border: 2px solid currentColor;
  border-bottom-color: transparent;
  border-radius: 50%;
  display: inline-block;
  animation: rotation 1s linear infinite;
  
  @keyframes rotation {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  isLoading = false,
  leftIcon,
  rightIcon,
  disabled,
  ...rest
}) => {
  return (
    <StyledButton
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      isLoading={isLoading}
      disabled={disabled || isLoading}
      {...rest}
    >
      {isLoading && <LoadingSpinner />}
      {!isLoading && leftIcon}
      {children}
      {!isLoading && rightIcon}
    </StyledButton>
  );
};

export default Button; 